import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';

import type {
  AdminApi,
  AdminAuditEvent,
  ArchivedCaseBundle,
  ArchivedInspectionCaseSummary,
  ChecklistInstanceDetail,
  DocumentArtifact,
  EmailDelivery,
  InspectionCaseDetail,
  InspectionCaseSummary,
  InspectionDecision,
  InspectionResultVersion,
  SignatureRecord,
  TemplateVersionSummary,
} from './admin-api';
import {
  ClientPager,
  InspectionCasesTable,
  matchCaseQuery,
  uniqueContractorOptions,
  useClientPage,
} from './admin-ops-case-table';
import {
  bookingStatusLabel,
  caseStatusLabel,
  checklistStatusLabel,
  decisionLabel,
  documentStatusLabel,
  emailStatusLabel,
  eventTypeLabel,
  formatYekaterinburg,
  problemMessage,
  problemTechnicalDetail,
  snapshotText,
} from './admin-presentation';
import { PageHeader, StatusChip, statusToneClass, TechnicalDetails } from './admin-ui';
import { navigate } from './path-router';

type OperationalSection = 'overview' | 'checklists' | 'result' | 'document' | 'email' | 'audit';

const SECTIONS: readonly { readonly id: OperationalSection; readonly label: string }[] = [
  { id: 'overview', label: 'Обзор' },
  { id: 'checklists', label: 'Чек-листы' },
  { id: 'result', label: 'Результат' },
  { id: 'document', label: 'Документ' },
  { id: 'email', label: 'Email' },
  { id: 'audit', label: 'Аудит' },
];

function revokeObjectUrl(url: string): void {
  if (typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  if (typeof props.failure === 'string') {
    return (
      <div role="alert" className="banner banner-closed">
        <p>{props.failure}</p>
      </div>
    );
  }
  const technical = problemTechnicalDetail(props.failure);
  return (
    <div role="alert" className="banner banner-closed">
      <p>{problemMessage(props.failure)}</p>
      {technical !== null ? (
        <TechnicalDetails>
          <p>{technical}</p>
        </TechnicalDetails>
      ) : null}
    </div>
  );
}

export function InspectionsAdmin(props: { readonly api: AdminApi; readonly path: string }) {
  const caseId = props.path.startsWith('/admin/inspections/')
    ? props.path.slice('/admin/inspections/'.length)
    : null;
  if (caseId !== null && caseId.length > 0) {
    return <ActiveInspectionDetail api={props.api} caseId={caseId} />;
  }
  return <InspectionList api={props.api} />;
}

export function ArchiveAdmin(props: { readonly api: AdminApi; readonly path: string }) {
  const caseId = props.path.startsWith('/admin/archive/')
    ? props.path.slice('/admin/archive/'.length)
    : null;
  if (caseId !== null && caseId.length > 0) {
    return <ArchiveDetail api={props.api} caseId={caseId} />;
  }
  return <ArchiveSearch api={props.api} />;
}

function InspectionList(props: { readonly api: AdminApi }) {
  const [rows, setRows] = useState<InspectionCaseSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [queryDraft, setQueryDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [contractorDraft, setContractorDraft] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [contractor, setContractor] = useState('');

  const reload = () => {
    setError(null);
    void props.api
      .listInspectionCases()
      .then((items) => setRows(Array.isArray(items) ? items : []))
      .catch((failure: unknown) => {
        setRows(null);
        setError(failure);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api]);

  const filtered = useMemo(() => {
    if (rows === null) {
      return [];
    }
    return rows.filter((row) => {
      if (status.trim().length > 0 && row.status !== status) {
        return false;
      }
      if (!matchCaseQuery(row, query)) {
        return false;
      }
      if (contractor.trim().length > 0) {
        const name = snapshotText(row.contractor, ['legal_name', 'name', 'display_name']) ?? '';
        if (name !== contractor) {
          return false;
        }
      }
      return true;
    });
  }, [rows, query, contractor, status]);

  const contractorOptions = useMemo(
    () => uniqueContractorOptions(rows ?? []),
    [rows],
  );
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows ?? []) {
      set.add(row.status);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const pager = useClientPage(filtered);

  useEffect(() => {
    pager.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when filter inputs change
  }, [query, status, contractor, rows]);

  return (
    <div className="pkg-c-inspections">
      <PageHeader
        title="Осмотры"
        description="Активные и текущие осмотры с быстрым поиском в загруженной выборке."
      />
      <form
        className="pkg-c-filter-bar"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(queryDraft);
          setStatus(statusDraft);
          setContractor(contractorDraft);
        }}
      >
        <div className="pkg-c-filter-fields">
          <label className="pkg-c-filter-query" htmlFor="insp_query">
            Поиск
            <input
              id="insp_query"
              aria-label="Поиск осмотров"
              placeholder="Номер, госномер, подрядчик"
              value={queryDraft}
              onChange={(event) => setQueryDraft(event.target.value)}
            />
          </label>
          <label htmlFor="insp_status">
            Статус
            <select
              id="insp_status"
              aria-label="Статус осмотра"
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
            >
              <option value="">Все статусы</option>
              {statusOptions.map((code) => (
                <option key={code} value={code}>
                  {caseStatusLabel(code)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="insp_contractor">
            Подрядчик
            <select
              id="insp_contractor"
              aria-label="Подрядчик в загруженной выборке"
              value={contractorDraft}
              onChange={(event) => setContractorDraft(event.target.value)}
            >
              <option value="">Все подрядчики (в выборке)</option>
              {contractorOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="meta pkg-c-filter-note">
          Поиск, статус и подрядчик применяются к загруженному списку inspection-cases (не
          глобальный полнотекстовый индекс).
        </p>
        <div className="pkg-c-filter-actions">
          <button type="submit">Найти</button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setQueryDraft('');
              setStatusDraft('');
              setContractorDraft('');
              setQuery('');
              setStatus('');
              setContractor('');
            }}
          >
            Сбросить
          </button>
        </div>
      </form>
      {error !== null ? (
        <div>
          <ProblemAlert failure={error} />
          <button type="button" onClick={reload}>
            Повторить
          </button>
        </div>
      ) : null}
      {rows === null && error === null ? <p role="status">Загрузка…</p> : null}
      {rows !== null ? (
        <section className="pkg-c-panel">
          <p className="meta" role="status">
            В загруженной выборке после фильтров: {filtered.length}
            {rows.length !== filtered.length ? ` (из ${rows.length} загруженных)` : ''}
          </p>
          <InspectionCasesTable
            rows={pager.pageRows}
            kind="inspections"
            emptyLabel="Осмотры не найдены."
          />
          <ClientPager
            page={pager.page}
            totalPages={pager.totalPages}
            total={filtered.length}
            onPage={pager.setPage}
            scopeNote="Пагинация клиентская по текущей выборке."
          />
        </section>
      ) : null}
    </div>
  );
}

function ArchiveSearch(props: { readonly api: AdminApi }) {
  const [caseNumber, setCaseNumber] = useState('');
  const [plate, setPlate] = useState('');
  const [decision, setDecision] = useState('');
  const [applied, setApplied] = useState({ caseNumber: '', plate: '', decision: '' });
  const [rows, setRows] = useState<ArchivedInspectionCaseSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const search = (filters: { caseNumber: string; plate: string; decision: string }) => {
    setBusy(true);
    setError(null);
    void props.api
      .searchArchive({
        caseNumber: filters.caseNumber.trim() || undefined,
        vehicleRegistrationNumber: filters.plate.trim() || undefined,
        decision: filters.decision.length === 0 ? undefined : filters.decision,
      })
      .then((items) => setRows(Array.isArray(items) ? items : []))
      .catch((failure: unknown) => {
        setRows(null);
        setError(failure);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  useEffect(() => {
    search(applied);
  }, [props.api, applied.caseNumber, applied.plate, applied.decision]);

  const pager = useClientPage(rows ?? []);

  useEffect(() => {
    pager.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when result set changes
  }, [rows]);

  return (
    <div className="pkg-c-archive">
      <PageHeader
        title="Архив"
        description="Завершённые и архивные осмотры для поиска и просмотра истории."
      />
      <form
        className="pkg-c-filter-bar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({ caseNumber, plate, decision });
        }}
      >
        <div className="pkg-c-filter-fields">
          <label htmlFor="archive_case_number">
            Номер осмотра
            <input
              id="archive_case_number"
              value={caseNumber}
              disabled={busy}
              placeholder="Введите номер осмотра"
              onChange={(event) => setCaseNumber(event.target.value)}
            />
          </label>
          <label htmlFor="archive_vehicle">
            Госномер
            <input
              id="archive_vehicle"
              value={plate}
              disabled={busy}
              placeholder="Например, А777АА77"
              onChange={(event) => setPlate(event.target.value)}
            />
          </label>
          <label htmlFor="archive_decision">
            Решение
            <select
              id="archive_decision"
              value={decision}
              disabled={busy}
              onChange={(event) => setDecision(event.target.value)}
            >
              <option value="">Все</option>
              <option value="APPROVED">Допущено</option>
              <option value="REJECTED">Не допущено</option>
            </select>
          </label>
        </div>
        <p className="meta pkg-c-filter-note">
          Фильтр «Подрядчик» не показан: searchArchive не принимает contractor. Экспорт не
          доступен — кнопки выгрузки нет.
        </p>
        <div className="pkg-c-filter-actions">
          <button type="submit" disabled={busy}>
            Найти
          </button>
          <button
            type="button"
            className="btn-ghost"
            disabled={busy}
            onClick={() => {
              setCaseNumber('');
              setPlate('');
              setDecision('');
              setApplied({ caseNumber: '', plate: '', decision: '' });
            }}
          >
            Сбросить
          </button>
        </div>
      </form>
      <p className="banner banner-open pkg-c-archive-note" role="note">
        Архив — источник исторического просмотра через searchArchive / archived bundle. Это не
        автоматический синоним любых завершённых осмотров из активного списка.
      </p>
      {error !== null ? (
        <div>
          <ProblemAlert failure={error} />
          <button type="button" onClick={() => search(applied)}>
            Повторить
          </button>
        </div>
      ) : null}
      {rows === null && error === null ? <p role="status">Загрузка…</p> : null}
      {rows !== null ? (
        <section className="pkg-c-panel">
          <p className="meta" role="status">
            В ответе архивного поиска: {rows.length}
          </p>
          <InspectionCasesTable
            rows={pager.pageRows}
            kind="archive"
            showDecision
            emptyLabel="Архивные осмотры не найдены."
          />
          <ClientPager
            page={pager.page}
            totalPages={pager.totalPages}
            total={rows.length}
            onPage={pager.setPage}
            scopeNote="Пагинация клиентская по ответу searchArchive. Колонка даты — время записи (расписание); completed_at в list DTO отсутствует."
          />
        </section>
      ) : null}
    </div>
  );
}

function ActiveInspectionDetail(props: { readonly api: AdminApi; readonly caseId: string }) {
  const [detail, setDetail] = useState<InspectionCaseDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [section, setSection] = useState<OperationalSection>('overview');

  useEffect(() => {
    setError(null);
    void props.api
      .getInspectionCase(props.caseId)
      .then(setDetail)
      .catch((failure: unknown) => {
        setDetail(null);
        setError(failure);
      });
  }, [props.api, props.caseId]);

  return (
    <CaseWorkspace
      title={detail?.case_number ?? 'Осмотр'}
      description={
        detail === null ? undefined : (
          <>
            <StatusChip
              label={caseStatusLabel(detail.status)}
              tone={statusToneClass(detail.status)}
            />{' '}
            · {bookingStatusLabel(detail.booking.status)} ·{' '}
            {formatYekaterinburg(detail.booking.scheduled_start_at)}
          </>
        )
      }
      backPath="/admin/inspections"
      backLabel="К списку осмотров"
      section={section}
      onSection={setSection}
      error={error}
      loading={detail === null && error === null}
      overview={detail === null ? null : <OverviewSection detail={detail} />}
      checklists={
        detail === null ? null : (
          <ChecklistsSection api={props.api} detail={detail} historical={false} />
        )
      }
      result={
        detail === null ? null : <ResultSection api={props.api} detail={detail} historical={null} />
      }
      document={
        detail === null ? null : (
          <DocumentSection api={props.api} detail={detail} historical={null} />
        )
      }
      email={
        detail === null ? null : (
          <EmailSection api={props.api} caseId={props.caseId} historical={null} />
        )
      }
      audit={detail === null ? null : <AuditSection api={props.api} caseId={props.caseId} />}
    />
  );
}

function ArchiveDetail(props: { readonly api: AdminApi; readonly caseId: string }) {
  const [bundle, setBundle] = useState<ArchivedCaseBundle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [section, setSection] = useState<OperationalSection>('overview');

  useEffect(() => {
    setError(null);
    void props.api
      .getArchivedCaseBundle(props.caseId)
      .then(setBundle)
      .catch((failure: unknown) => {
        setBundle(null);
        setError(failure);
      });
  }, [props.api, props.caseId]);

  const detail = bundle?.inspection_case ?? null;

  return (
    <CaseWorkspace
      title={detail?.case_number ?? 'Архивный осмотр'}
      backPath="/admin/archive"
      backLabel="К архиву"
      section={section}
      onSection={setSection}
      error={error}
      loading={bundle === null && error === null}
      overview={detail === null ? null : <OverviewSection detail={detail} />}
      checklists={
        detail === null ? null : (
          <ChecklistsSection api={props.api} detail={detail} historical={true} />
        )
      }
      result={
        detail === null || bundle === null ? null : (
          <ResultSection api={props.api} detail={detail} historical={bundle} />
        )
      }
      document={
        detail === null || bundle === null ? null : (
          <DocumentSection api={props.api} detail={detail} historical={bundle} />
        )
      }
      email={
        bundle === null ? null : (
          <EmailSection
            api={props.api}
            caseId={props.caseId}
            historical={bundle.email_deliveries}
          />
        )
      }
      audit={detail === null ? null : <AuditSection api={props.api} caseId={props.caseId} />}
    />
  );
}

function CaseWorkspace(props: {
  readonly title: string;
  readonly description?: ReactNode;
  readonly backPath: string;
  readonly backLabel: string;
  readonly section: OperationalSection;
  readonly onSection: (section: OperationalSection) => void;
  readonly error: unknown;
  readonly loading: boolean;
  readonly overview: ReactNode;
  readonly checklists: ReactNode;
  readonly result: ReactNode;
  readonly document: ReactNode;
  readonly email: ReactNode;
  readonly audit: ReactNode;
}) {
  return (
    <>
      <PageHeader
        title={props.title}
        description={props.description}
        back={{
          label: props.backLabel,
          onClick: () => {
            navigate(props.backPath);
          },
        }}
      />
      {props.error !== null ? <ProblemAlert failure={props.error} /> : null}
      {props.loading ? <p role="status">Загрузка…</p> : null}
      <div className="section-tabs" role="tablist" aria-label="Разделы осмотра">
        {SECTIONS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={props.section === item.id}
            className={
              props.section === item.id
                ? 'section-tab is-active nav-link nav-current'
                : 'section-tab nav-link'
            }
            onClick={() => {
              props.onSection(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {props.section === 'overview' ? props.overview : null}
      {props.section === 'checklists' ? props.checklists : null}
      {props.section === 'result' ? props.result : null}
      {props.section === 'document' ? props.document : null}
      {props.section === 'email' ? props.email : null}
      {props.section === 'audit' ? props.audit : null}
    </>
  );
}

function OverviewSection(props: { readonly detail: InspectionCaseDetail }) {
  const plate =
    props.detail.vehicle?.registration_number ??
    snapshotText(props.detail.vehicle_snapshot, ['registration_number', 'registration_plate']);
  const contractor =
    snapshotText(props.detail.contractor, ['legal_name', 'name']) ??
    snapshotText(props.detail.contractor_snapshot, ['legal_name', 'name']);
  const statusLabel = caseStatusLabel(props.detail.status);

  return (
    <section className="info-card">
      <h2 className="section-title">Обзор</h2>
      <dl className="info-list">
        <div>
          <dt>Статус</dt>
          <dd>
            <StatusChip label={statusLabel} tone={statusToneClass(props.detail.status)} />
          </dd>
        </div>
        <div>
          <dt>Запись</dt>
          <dd>
            {bookingStatusLabel(props.detail.booking.status)} ·{' '}
            {formatYekaterinburg(props.detail.booking.scheduled_start_at)}
          </dd>
        </div>
        <div>
          <dt>Госномер</dt>
          <dd>{plate ?? '—'}</dd>
        </div>
        <div>
          <dt>Подрядчик</dt>
          <dd>{contractor ?? '—'}</dd>
        </div>
        {props.detail.completed_at !== undefined && props.detail.completed_at !== null ? (
          <div>
            <dt>Завершён</dt>
            <dd>{formatYekaterinburg(props.detail.completed_at)}</dd>
          </div>
        ) : null}
        {props.detail.final_comment !== undefined && props.detail.final_comment !== null ? (
          <div>
            <dt>Комментарий</dt>
            <dd>{props.detail.final_comment}</dd>
          </div>
        ) : null}
      </dl>
      <TechnicalDetails>
        <p className="meta">Идентификатор осмотра: {props.detail.id}</p>
        <p className="meta">Версия: {props.detail.version}</p>
      </TechnicalDetails>
    </section>
  );
}

function ChecklistsSection(props: {
  readonly api: AdminApi;
  readonly detail: InspectionCaseDetail;
  readonly historical: boolean;
}) {
  const [rows, setRows] = useState<
    | readonly {
        instance: ChecklistInstanceDetail | ChecklistInstanceSummaryLike;
        template: TemplateVersionSummary | null;
      }[]
    | null
  >(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError(null);
      try {
        const loaded = await Promise.all(
          props.detail.checklist_instances.map(async (item) => {
            const instance = props.historical
              ? item
              : await props.api.getChecklistInstance(item.id);
            const template = props.historical
              ? null
              : await props.api.getTemplateVersion(item.template_version_id);
            return { instance, template };
          }),
        );
        if (!cancelled) {
          setRows(loaded);
        }
      } catch (failure) {
        if (!cancelled) {
          setError(failure);
          setRows(null);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [props.api, props.detail, props.historical]);

  const assignmentFor = (checklistId: string) =>
    props.detail.assignments.find(
      (row) => row.checklist_instance_id === checklistId && row.unassigned_at === null,
    );

  return (
    <section>
      <h2>Чек-листы</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {rows === null && error === null ? <p role="status">Загрузка…</p> : null}
      {rows !== null && rows.length === 0 ? (
        <p role="status" className="banner">
          Чек-листы не назначены.
        </p>
      ) : null}
      {rows?.map(({ instance, template }) => {
        const assignment = assignmentFor(instance.id);
        return (
          <article key={instance.id} className="banner">
            <p>
              {template?.template_code ?? 'Шаблон'}
              {template !== null ? ` · версия ${template.version_code}` : ''}
            </p>
            {'status' in instance && instance.status !== undefined ? (
              <p>Статус: {checklistStatusLabel(instance.status)}</p>
            ) : null}
            {'version' in instance && instance.version !== undefined ? (
              <p className="meta">Версия экземпляра: {instance.version}</p>
            ) : null}
            {assignment !== undefined ? (
              <p className="meta">Назначение: {assignment.inspection_role_code}</p>
            ) : null}
            <details>
              <summary>Технические сведения</summary>
              <p className="meta">Экземпляр: {instance.id}</p>
              <p className="meta">Версия шаблона: {instance.template_version_id}</p>
            </details>
          </article>
        );
      })}
    </section>
  );
}

type ChecklistInstanceSummaryLike = {
  readonly id: string;
  readonly template_version_id: string;
  readonly status?: string;
  readonly version?: string;
};

function ResultSection(props: {
  readonly api: AdminApi;
  readonly detail: InspectionCaseDetail;
  readonly historical: ArchivedCaseBundle | null;
}) {
  const [decision, setDecision] = useState<InspectionDecision | null>(
    props.historical?.decision ?? props.detail.decision ?? null,
  );
  const [result, setResult] = useState<InspectionResultVersion | null>(
    props.historical?.result_version ?? null,
  );
  const [signature, setSignature] = useState<SignatureRecord | null>(
    props.historical?.signature_record ?? null,
  );
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(props.historical === null);

  useEffect(() => {
    if (props.historical !== null) {
      setDecision(props.historical.decision);
      setResult(props.historical.result_version);
      setSignature(props.historical.signature_record);
      setLoading(false);
      return;
    }
    const hasCanonicalResult =
      (props.detail.decision !== null && props.detail.decision !== undefined) ||
      (props.detail.result_version_id !== null && props.detail.result_version_id !== undefined);
    if (!hasCanonicalResult) {
      setDecision(null);
      setResult(null);
      setSignature(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextDecision = await props.api.getInspectionDecision(props.detail.id);
        const nextResult = await props.api.getInspectionResultVersion(props.detail.id);
        let nextSignature: SignatureRecord | null = null;
        if (
          props.detail.signature_record_id !== null &&
          props.detail.signature_record_id !== undefined
        ) {
          nextSignature = await props.api.getSignatureRecord(props.detail.signature_record_id);
        }
        if (!cancelled) {
          setDecision(nextDecision);
          setResult(nextResult);
          setSignature(nextSignature);
        }
      } catch (failure) {
        if (!cancelled) {
          setError(failure);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [props.api, props.detail, props.historical]);

  return (
    <section>
      <h2>Результат</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {loading ? <p role="status">Загрузка…</p> : null}
      {!loading && decision === null && error === null ? (
        <p role="status" className="banner">
          Решение ещё не зафиксировано.
        </p>
      ) : null}
      {decision !== null ? (
        <>
          <p>Решение: {decisionLabel(decision.decision)}</p>
          <p>Дата решения: {formatYekaterinburg(decision.decided_at)}</p>
        </>
      ) : null}
      {result !== null ? <p>Редакция результата: {result.business_revision}</p> : null}
      {signature !== null ? <p>Подпись: {formatYekaterinburg(signature.signed_at)}</p> : null}
      <details>
        <summary>Технические сведения</summary>
        {decision !== null ? <p className="meta">Решение: {decision.id}</p> : null}
        {result !== null ? (
          <>
            <p className="meta">Результат: {result.id}</p>
            <p className="meta">Контрольная сумма: {result.payload_checksum}</p>
            <p className="meta">Схема: {result.payload_schema_version}</p>
          </>
        ) : null}
        {signature !== null ? <p className="meta">Подпись: {signature.id}</p> : null}
        {props.detail.document_artifact_id !== undefined &&
        props.detail.document_artifact_id !== null ? (
          <p className="meta">Документ: {props.detail.document_artifact_id}</p>
        ) : null}
      </details>
    </section>
  );
}

function DocumentSection(props: {
  readonly api: AdminApi;
  readonly detail: InspectionCaseDetail;
  readonly historical: ArchivedCaseBundle | null;
}) {
  const artifactId =
    props.historical?.document_artifact?.id ?? props.detail.document_artifact_id ?? null;
  const [artifact, setArtifact] = useState<DocumentArtifact | null>(
    props.historical?.document_artifact ?? null,
  );
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrl !== null) {
        revokeObjectUrl(objectUrl);
      }
    };
  }, [objectUrl]);

  const reload = () => {
    if (artifactId === null || props.historical !== null) {
      return;
    }
    setError(null);
    void props.api
      .getDocumentArtifact(artifactId)
      .then(setArtifact)
      .catch((failure: unknown) => {
        setError(failure);
      });
  };

  useEffect(() => {
    if (props.historical !== null) {
      setArtifact(props.historical.document_artifact);
      return;
    }
    reload();
  }, [props.api, artifactId, props.historical]);

  const openOrDownload = async (download: boolean) => {
    if (artifactId === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const content = await props.api.downloadDocumentContent(artifactId);
      const url = URL.createObjectURL(content.blob);
      if (objectUrl !== null) {
        revokeObjectUrl(objectUrl);
      }
      setObjectUrl(url);
      if (download) {
        const link = document.createElement('a');
        link.href = url;
        link.download = content.filename ?? `${props.detail.case_number}.pdf`;
        link.click();
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (artifact === null) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await props.api.retryPdfGeneration(artifact.id, artifact.version);
      setArtifact(next);
      setNotice('Повторное формирование принято.');
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2>Документ</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {notice !== null ? (
        <p role="status" className="banner">
          {notice}
        </p>
      ) : null}
      {artifact === null && error === null ? (
        <p role="status" className="banner">
          Документ ещё не создан.
        </p>
      ) : null}
      {artifact !== null ? (
        <>
          <p>Статус: {documentStatusLabel(artifact.generation_status)}</p>
          {artifact.generation_status === 'GENERATED' ? (
            <div className="actions">
              <button type="button" disabled={busy} onClick={() => void openOrDownload(false)}>
                Открыть PDF
              </button>
              <button type="button" disabled={busy} onClick={() => void openOrDownload(true)}>
                Скачать PDF
              </button>
            </div>
          ) : null}
          {artifact.generation_status === 'GENERATION_ERROR' && props.historical === null ? (
            <>
              {artifact.last_error_message !== null && artifact.last_error_message !== undefined ? (
                <p>{artifact.last_error_message}</p>
              ) : null}
              <button type="button" disabled={busy} onClick={() => void retry()}>
                Повторить формирование
              </button>
            </>
          ) : null}
          <details>
            <summary>Технические сведения</summary>
            <p className="meta">Документ: {artifact.id}</p>
            <p className="meta">Версия: {artifact.version}</p>
            {artifact.content_checksum !== null && artifact.content_checksum !== undefined ? (
              <p className="meta">Контрольная сумма: {artifact.content_checksum}</p>
            ) : null}
          </details>
        </>
      ) : null}
    </section>
  );
}

function EmailSection(props: {
  readonly api: AdminApi;
  readonly caseId: string;
  readonly historical: readonly EmailDelivery[] | null;
}) {
  const [rows, setRows] = useState<EmailDelivery[] | null>(
    props.historical === null ? null : [...props.historical],
  );
  const [error, setError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = () => {
    if (props.historical !== null) {
      setRows([...props.historical]);
      return;
    }
    setError(null);
    void props.api
      .listCaseEmailDeliveries(props.caseId)
      .then(setRows)
      .catch((failure: unknown) => {
        setError(failure);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.caseId, props.historical]);

  const retry = async (delivery: EmailDelivery) => {
    setBusyId(delivery.id);
    setError(null);
    setNotice(null);
    try {
      const next = await props.api.retryEmailDelivery(delivery.id, delivery.version);
      setRows((current) => (current ?? []).map((row) => (row.id === next.id ? next : row)));
      setNotice('Повторная отправка принята.');
    } catch (failure) {
      setError(failure);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section>
      <h2>Email</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {notice !== null ? (
        <p role="status" className="banner">
          {notice}
        </p>
      ) : null}
      {rows === null && error === null ? <p role="status">Загрузка…</p> : null}
      {rows !== null && rows.length === 0 ? (
        <p role="status" className="banner">
          Отправки не найдены.
        </p>
      ) : null}
      {rows !== null && rows.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Получатели</th>
              <th>Статус</th>
              <th>Попытки</th>
              <th>Последняя попытка</th>
              <th>Отправлено</th>
              <th>Ошибка</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.recipient_addresses.join(', ')}</td>
                <td>{emailStatusLabel(row.status)}</td>
                <td>{row.attempt_count}</td>
                <td>{formatYekaterinburg(row.last_attempt_at)}</td>
                <td>{formatYekaterinburg(row.sent_at)}</td>
                <td>{row.last_error ?? '—'}</td>
                <td>
                  {row.status === 'SEND_ERROR' && props.historical === null ? (
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void retry(row)}
                    >
                      Повторить отправку
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

function AuditSection(props: { readonly api: AdminApi; readonly caseId: string }) {
  const [rows, setRows] = useState<AdminAuditEvent[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setError(null);
    void props.api
      .getCaseAudit(props.caseId)
      .then(setRows)
      .catch((failure: unknown) => {
        setError(failure);
      });
  }, [props.api, props.caseId]);

  return (
    <section>
      <h2>Аудит</h2>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {rows === null && error === null ? <p role="status">Загрузка…</p> : null}
      {rows !== null && rows.length === 0 ? (
        <p role="status" className="banner">
          События не найдены.
        </p>
      ) : null}
      {rows?.map((row) => (
        <article key={row.id} className="banner">
          <p>
            {eventTypeLabel(row.event_type)} · {formatYekaterinburg(row.occurred_at)}
          </p>
          <details>
            <summary>Технические сведения</summary>
            <p className="meta">Событие: {row.event_type}</p>
            <p className="meta">Идентификатор: {row.id}</p>
            <p className="meta">Тип актора: {row.actor_type}</p>
            {row.actor_user_id !== null ? <p className="meta">Актор: {row.actor_user_id}</p> : null}
            {row.subject_type.length > 0 ? (
              <p className="meta">Тип объекта: {row.subject_type}</p>
            ) : null}
            {row.subject_id !== null ? <p className="meta">Объект: {row.subject_id}</p> : null}
          </details>
        </article>
      ))}
    </section>
  );
}
